<template>
  <div
    ref="searchRoot"
    class="products-search">
    <div
      class="products-search__search-button"
      @click="$router.push({ hash: 'search' })" />

    <transition name="search-modal">
      <div
        v-if="isSearchVisible"
        ref="searchBody"
        class="products-search__wrapper">
        <label class="products-search__body">
          <input
            ref="searchInput"
            :value="searchValue"
            class="products-search__input"
            type="search"
            inputmode="search"
            placeholder="Search"
            autocomplete="off"
            @keypress.enter="showSearchResults"
            @focus="showSearch"
            @blur="hideSearch"
            @input="setSearchValue">

          <div
            class="products-search__close"
            @click.prevent="$router.go(-1)">
            <span class="shape-cross" />
          </div>
        </label>

        <transition name="transition--opacity">
          <div
            v-if="searchResults.length && hasFocus"
            class="products-search__results">
            <div
              v-for="product in searchResults"
              :key="product.id"
              class="products-search__results-item"
              @mousedown="navigateToOffer(product)">
              <div class="products-search__result-title">
                {{ product.title }}
              </div>
              <div class="products-search__result-description transition--color">
                {{ lengthShorter(getTextFromHTML(product.product_contains || '')) }}
              </div>
            </div>
            <div
              class="button products-search__full"
              @mousedown="showSearchResults">
              View full results
            </div>
          </div>
        </transition>
      </div>
    </transition>

    <request-engine
      ref="engine"
      :url="requestUrl"
      :options="requestOptions"
      :should-request-on-mounted="false"
      @response="responseHandlerData" />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { getTextFromHTML } from 'Root/helpers/tools';
import SortModel from 'Models/SortModel';
import googleTagAnalytics from 'Root/drivers/googleTagAnalytics';
import RequestEngine from 'Root/pages/game/components/RequestEngine.vue';
import AppModel from 'Models/AppModel';
import GameModel from 'Models/GameModel';
import { flatTagsById } from 'Root/helpers/tags';

export default {
  name: 'ProductsSearch',

  components: {
    RequestEngine,
  },

  props: {
    searchResultsNumber: {
      type: Number,
      default: 4,
    },
  },

  data() {
    return {
      requestUrl: `${AppModel.API_URI}/product/list`,
      hasFocus: false,
      searchValue: '',
      searchResults: [],
      searchOptions: {
        sortType: SortModel.TYPE_POPULARITY,
        sortDesc: true,
      },
      getTextFromHTML,
    };
  },

  computed: {
    ...mapState(['activeRegion', 'activeGame', 'userDevice']),

    isMobile() {
      return this.userDevice.innerWidth < 1280;
    },

    isSearchVisible() {
      if (this.isMobile) {
        return this.hasFocus;
      }

      return true;
    },

    requestOptions() {
      if (!this.isSearchVisible || !this.searchValue) {
        return {};
      }

      return {
        search: {
          value: this.searchValue,
          debounce: 500,
        },
        sort: {
          value: this.searchOptions.sortType,
        },
        desc: {
          value: this.searchOptions.sortDesc,
        },
        offset: {
          value: 0,
        },
        limit: {
          value: this.searchResultsNumber,
        },
        region: {
          value: this.activeRegion.id,
        },
        with_game: {
          value: this.activeGame.id,
        },
      };
    },
  },

  watch: {
    '$route.hash': {
      handler(newVal) {
        if (!this.isMobile) {
          return;
        }

        if (newVal === '#search') {
          this.showSearch();
        } else if (this.hasFocus) {
          this.hideSearch();
        }
      },
    },
  },

  methods: {
    emitSearchFocus() {
      googleTagAnalytics.emitSearchActivated();
    },

    lengthShorter(str) {
      return str.length > 100
        ? `${str.substr(0, 100)}...`
        : str;
    },

    responseHandlerData(data) {
      this.searchResults = data.result;
    },

    showSearch(event) {
      if (this.isMobile) {
        if (event?.type === 'focus') {
          return;
        }

        this.emitSearchFocus();
        this.hasFocus = true;
        document.body.classList.add('no-scroll');

        this.$nextTick(() => {
          this.$refs.searchInput.focus();
        });
        return;
      }

      this.emitSearchFocus();

      const { searchRoot, searchBody } = this.$refs;
      searchBody.addEventListener('transitionend', () => {
        if (event.target === document.activeElement) {
          this.hasFocus = true;
          searchRoot.classList.add('products-search--active');
        }
      }, { once: true });
    },

    hideSearch(event) {
      if (this.isMobile && event && !event.target.closest('.products-search__close')) {
        return;
      }

      this.hasFocus = false;
      this.$refs.searchRoot.classList.remove('products-search--active');
      document.body.classList.remove('no-scroll');
    },

    showSearchResults() {
      if (this.$route.params.query !== this.searchValue) {
        this.$emit('close');
        this.$refs.searchInput.blur();

        if (this.searchValue) {
          this.$router.push({ name: 'search', params: { query: this.searchValue } });
        } else {
          this.$router.push({ name: 'search' });
        }
      }
    },

    navigateToOffer(product) {
      this.$emit('close');
      this.hideSearch();

      if (!product.show_as_tag?.id) {
        this.$router.push({
          name: 'game__product-detail',
          params: {
            namespace: GameModel.ID_TO_DATA[product.game].routeNamespace,
            id: product?.title_slug ? `${product?.title_slug}-${product.id}` : product.id,
          },
        });
      }

      return this.$router.push(flatTagsById[product.show_as_tag.id]._href);
    },

    setSearchValue(event) {
      this.searchValue = event.target.value.trim();
    },
  },
};
</script>

<style lang="scss" scoped>
  $searchIconWidth: 31px;

  .products-search {
    overflow: hidden;

    &--active {
      overflow: visible;
    }
  }

  .products-search__wrapper {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: $color--basic-black;
    padding: 20px;
    padding-bottom: 30px;
    flex-direction: column;
    z-index: 15;
  }

  .products-search__body {
    display: block;
    width: 100%;
  }

  .products-search__search-button {
    width: 20px;
    height: 20px;
    background: url(/images/icons/product-search.svg) no-repeat 0 50%;
    background-size: contain;
    cursor: pointer;
  }

  .products-search__close {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 50px;
    height: 100%;
    position: absolute;
    top: 0;
    right: 0;

    .shape-cross {
      --shape-color: #{$color--basic-light-gray};

      width: 16px;
      height: 16px;
    }
  }

  .products-search__input {
    border: none;
    font-weight: 500;
    color: $color--basic-white;
    outline: none;
    padding-left: calc(#{$searchIconWidth} + 20px);
    padding-right: 50px;
    line-height: 40px;
    background-color: $color--basic-menu;
    border-radius: 100px;
    font-size: $text-13px;
    width: 100%;
    font-family: inherit;

    &::placeholder {
      color: $color--basic-white;
    }

  }

  .products-search__results {
    margin-top: 20px;
  }

  .products-search__results-item {
    cursor: pointer;
    border-top: 1px solid $color--basic-menu;
    margin-top: 12px;
    padding-top: 12px;

    &:first-child {
      margin-top: 0;
      padding-top: 0;
      border: none;
    }
  }

  .products-search__result-title {
    font-size: 15px;
    font-weight: 500;
  }

  .products-search__result-description {
    margin: 8px 0 0;
    font-size: $text-13px;
    color: $color--basic-dark-gray;
  }

  .products-search__full {
    background-color: $color--basic-blue;
    font-size: $text-13px;
    text-transform: unset;
    width: fit-content;
    padding: 13px;
    margin-top: 30px;
    margin-right: auto;
    margin-left: auto;
  }

  @media screen and #{$screen-more-or-1280} {
    .products-search__wrapper {
      display: flex;
      flex-direction: initial;
      align-items: center;
      width: initial;
      height: initial;
      transform: translateX(100%) translateX(-$searchIconWidth);
      background: url(/images/icons/product-search.svg) no-repeat 0 50%;
      position: relative;
      padding: 0;
      transition: transform .2s ease-out;

      &:after {
        content: '';
        display: block;
        position: absolute;
        bottom: 0;
        left: $searchIconWidth;
        width: calc(100% - #{$searchIconWidth});
        height: 0;
        border-bottom: 2px solid $color--basic-dark-gray;
      }

      &:focus-within {
        transform: translateX(0);
      }
    }

    .products-search__search-button {
      display: none;
    }

    .products-search__input {
      flex: 1;
      line-height: 30px;
      background-color: transparent;
      border-radius: 0;
      font-size: 19px;
      padding-right: 0;
    }

    .products-search__close {
      display: none;
    }

    .products-search__results {
      position: absolute;
      top: 100%;
      left: $searchIconWidth;
      background-color: rgba($color--additional-gray, .95);
      padding: 10px 20px;
      margin-top: 12px;
    }

    .products-search__results-item {
      border-color: $color--basic-dark-gray;
    }

    .products-search__result-description {
      color: $color--basic-light-gray;
      font-size: $text-12px;
    }

    .products-search__full {
      font-size: $text-12px;
      padding: 8px 12px;
      margin-top: 12px;
      width: 100%;
    }
  }

 @media only screen and #{$screen-less-than-1280} {
    .search-modal-enter-active,
    .search-modal-leave-active {
      transition: var(--transition-opacity), transform var(--transition-opacity-time);
    }

    .search-modal-enter,
    .search-modal-leave-to {
      opacity: 0;
      transform: translateY(20px);
    }

    .products-search__body {
      display: block;
      position: relative;

      &:before {
        content: '';
        display: block;
        width: 50px;
        height: 100%;
        background-color: $color--basic-light-gray;
        -webkit-mask: url('/images/icons/product-search.svg')  no-repeat center;
        mask: url('/images/icons/product-search.svg')  no-repeat center;
        -webkit-mask-size: 16px 16px;
        mask-size: 16px 16px;
        position: absolute;
        top: 0;
        left: 0;
      }
    }
 }
</style>
